import React, {
  FC,
  useCallback,
  // useContext,
  useEffect,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { SelectOption } from 'types';
import {
  getProfileData, profileSetState, savePassword, saveProfileInfo, 
} from 'store/profile/actionCreators';
import { profileSelectors } from 'store/profile/selectors';
import { authSelectors } from 'store/auth/selectors';
import { getCountry } from 'store/auth/actionCreators';

import {
  Button,
  Dropzone,
  Input, Select,
  TitleText,
  toastError,
  toastSuccess,
} from 'components';
import { WhiteContainer } from 'containers';
// import { ModalContext } from 'context';
// import { PassVerificationModal } from 'modals/PassVerificationModal';
import { verifFailIcon, verifPassIcon } from 'assets/images';
import { emailValidator } from '../../utils';

import { profileCustomStyles } from './profileCustomStyles';
import styles from './styles.module.scss';

export const ProfilePage: FC = () => {
  const { t } = useTranslation('profile');
  const trans = useTranslation('welcome').t;
  const transDep = useTranslation('deposit').t;
  const dispatch = useDispatch();

  const avatar = useSelector(profileSelectors.getProp('avatar'));
  const lastName = useSelector(profileSelectors.getProp('lastName'));
  const firstName = useSelector(profileSelectors.getProp('firstName'));
  const email = useSelector(profileSelectors.getProp('email'));
  const phone = useSelector(profileSelectors.getProp('phone'));
  const isLoading = useSelector(profileSelectors.getProp('isLoading'));
  const country = useSelector(profileSelectors.getProp('countryProfile'));
  const countries = useSelector(authSelectors.getProp('country'));
  const isCompany = useSelector(profileSelectors.getProp('isCompany'));
  const challenge = useSelector(profileSelectors.getProp('challenge'));
  // const isVerified = useSelector(profileSelectors.getProp('isVerified'));
  const companyName = useSelector(profileSelectors.getProp('companyName'));
  const referralCode = useSelector(profileSelectors.getProp('referralCode'));
  const tin = useSelector(profileSelectors.getProp('tin'));
  const dataActive = useSelector(profileSelectors.getProp('dataActive'));
  const isQualificationExpired = dataActive && Date.now() >= new Date(dataActive).getTime();

  const hubURL = process.env.REACT_APP_HUB_URL;
  const qualificationLink = `${hubURL}/?ref=${referralCode}&self=true`;

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [repeatNewPassword, setRepeatNewPassword] = useState('');
  const [phoneReplace, setPhoneReplace] = useState('');

  let qualificationText = '';
  if (challenge) {
    qualificationText = t('Challenge_');
  } else if (isQualificationExpired) {
    qualificationText = t('Qualified_');
  } else {
    qualificationText = t('Not_qualified_');
  }

  const onChangeCountry = useCallback((value: SelectOption<string>) => {
    dispatch(profileSetState({
      countryProfile: value.value,
    }));
  }, [dispatch]);

  const onChangeLastName = useCallback((str: string) => {
    dispatch(profileSetState({
      lastName: str,
    }));
  }, [dispatch]);

  const onChangeFirstName = useCallback((str: string) => {
    dispatch(profileSetState({
      firstName: str,
    }));
  }, [dispatch]);

  const onChangeEmail = useCallback((str: string) => {
    dispatch(profileSetState({
      email: str,
    }));
  }, [dispatch]);

  const onChangePhone = useCallback((str: string) => {
    const cleanedStr = str.replace(/[^\d+]/g, '');
    setPhoneReplace(cleanedStr);
  }, []);

  const onChangeCompanyName = useCallback((str: string) => {
    dispatch(profileSetState({
      companyName: str,
    }));
  }, [dispatch]);

  const onChangeTin = useCallback((str: string) => {
    dispatch(profileSetState({
      tin: str,
    }));
  }, [dispatch]);

  useEffect(() => {
    dispatch(profileSetState({
      phone: phoneReplace,
    }));
  }, [phoneReplace, dispatch]);

  useEffect(() => {
    dispatch(getProfileData());
    dispatch(getCountry());
  }, [dispatch]);

  const onSubmitPersonalInfo = useCallback(() => {    
    const currentEmailError = emailValidator(email);

    if (currentEmailError) {
      toastError(trans(currentEmailError));
      return;
    }

    dispatch(saveProfileInfo({
      first_name: firstName,
      last_name: lastName,
      email,
      phone,
      country,
      company_name: companyName,
      tin,
      onCallback: () => {
        toastSuccess(t('Saved_change_'));
      },
    }));
  }, [email, dispatch, firstName, lastName, phone, country, companyName, tin, trans, t]);

  const onSubmitPassword = useCallback(() => {
    if (!newPassword && !repeatNewPassword) return;

    if (newPassword !== repeatNewPassword) {
      toastError(trans('Sign_in_password_mismatch_'));
      return;
    }

    dispatch(savePassword({
      old_password: oldPassword,
      password: newPassword,
      password_confirm: repeatNewPassword,
      onCallback: () => {
        toastSuccess(t('Saved_change_'));
        setOldPassword('');
        setNewPassword('');
        setRepeatNewPassword('');
      },
    }));
  }, [newPassword, repeatNewPassword, dispatch, oldPassword, trans, t]);

  // const { openJsxModal } = useContext(ModalContext);

  // const handleVerification = useCallback(() => {
  //   openJsxModal(<PassVerificationModal />);
  // }, [openJsxModal]);

  const handleQualification = useCallback(() => {
    window.open(qualificationLink, '_blank');
  }, [qualificationLink]);

  return (
    <div className={styles.profile}>
      <div className={styles.profile_container}>
        <Dropzone
          data={avatar}
          textInfo={t('Avatar_info_')}
          textBtn={t('Select_file_')}
        />
      </div>

      <WhiteContainer className={styles.profile_container}>
        <TitleText text={t('Verification_')} />
        <div className={styles.profile_form}>
          {/* <div className={styles.user_verification_container}>
            <p>{isVerified ? t('Verified_') : t('Not_verified_')}</p>
            {!isVerified && (
            <Button
              onClick={handleVerification}
              color="pinkTransparent"
            >
              {t('Pass_verification_')}
            </Button>
            )}
            <img
              alt=""
              src={isVerified ? verifPassIcon : verifFailIcon}
              width={40}
              height={40}
            />
          </div> */}
          <div className={styles.user_verification_container}>
            <p>{qualificationText}</p>
            {!isQualificationExpired && (
            <Button
              onClick={handleQualification}
              color="pinkTransparent"
            >
              {t('Pass_qualification_')}
            </Button>
            )}
            <img
              alt=""
              src={isQualificationExpired ? verifPassIcon : verifFailIcon}
              width={40}
              height={40}
            />
          </div>
        </div>
      </WhiteContainer>

      <div className={styles.profile_white_containers}>
        <WhiteContainer className={styles.profile_container}>
          <div className={styles.profile_white_container}>
            <div>
              <TitleText text={t('Personal_info_')} />
              <div className={styles.profile_form}>
                <Input
                  value={firstName}
                  onChangeValue={onChangeFirstName}
                  label={t('First_name_')}
                />
                <Input
                  value={lastName}
                  onChangeValue={onChangeLastName}
                  label={t('Last_name_')}
                />

                {isCompany && (
                <Input
                  value={companyName}
                  onChangeValue={onChangeCompanyName} 
                  label={t('Company_name_')}
                />
                )}
                {isCompany && (
                <Input
                  value={tin}
                  onChangeValue={onChangeTin}
                  label={t('TIN_')}
                  isNumberOnly
                  maxLength={8}
                />
                )}
                
                <Input
                  value={email}
                  onChangeValue={onChangeEmail}
                  label={t('Email_')}
                />
                <Input
                  value={phone}
                  onChangeValue={onChangePhone}
                  label={t('Phone_number_')}
                />
                <Select
                  options={countries}
                  onChange={onChangeCountry}
                  value={country ? { value: country, label: country } : null}
                  style={{ ...profileCustomStyles }}
                  placeholder={transDep('Country_')}
                  isSearchable
                />
              </div>
            </div>
            <div className={styles.profile_form_button_container}>
              <Button
                color="pink"
                onClick={onSubmitPersonalInfo}
                isLoading={isLoading}
              >
                {t('Save_changes_')}
              </Button>
            </div>
          </div>
        </WhiteContainer>

        <WhiteContainer className={styles.profile_container}>
          <div className={styles.profile_white_container}>
            <div>
              <TitleText text={t('Password_')} />
              <div className={styles.profile_form}>
                <Input
                  value={oldPassword}
                  onChangeValue={setOldPassword}
                  label={t('Old_password_')}
                />
                <Input
                  value={newPassword}
                  onChangeValue={setNewPassword}
                  label={t('New_password_')}
                />
                <Input
                  value={repeatNewPassword}
                  onChangeValue={setRepeatNewPassword}
                  label={t('Repeat_new_password_')}
                />
              </div>

            </div>
            <div className={styles.profile_form_button_container}>
              <Button
                color="pink"
                onClick={onSubmitPassword}
                isLoading={isLoading}
              >
                {t('Save_changes_')}
              </Button>
            </div>
          </div>
        </WhiteContainer>
      </div>
    </div>
  );
};
